import React, { useState } from 'react';
import cn from 'classnames';
import styles from './index.module.scss';

import { If } from 'utils';
import { PasswordStrength, UiKit } from 'components';
import { useForm, useMutation } from 'utils/hooks';
import { FORM_SCHEMA } from './stubs';

const DEFAULT_CANCEL_CALLBACK = () => {};

/**
 * @param {Object} props
 * @param {Object} [props.queryParams]
 * @param {function} [props.onSuccess]
 * @param {function} [props.onCancel]
 * @param {import('utils/types').Size} [props.buttonSize]
 */
export default function ChangePasswordForm({
  buttonSize = 'md',
  queryParams = {},
  onSuccess = () => {},
  onCancel = DEFAULT_CANCEL_CALLBACK
}) {
  const [isPasswordStrong, setIsPasswordStrong] = useState(false);

  const allowCancel = onCancel !== DEFAULT_CANCEL_CALLBACK;

  const { data, errors, onChange, onValidate } = useForm({
    dataSchema: FORM_SCHEMA,
    defaultData: {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: ''
    }
  });

  const { isProcessing, mutate } = useMutation('auth.changePassword', 'POST', {
    axiosConfig: { params: queryParams },
    payload: () => ({
      current_password: data.currentPassword,
      new_password: data.newPassword,
      re_new_password: data.confirmNewPassword
    }),
    errorMessage:
      'Unable to change password at the moment. Please try again later.',
    successMessage: 'Successfully updated the password.',
    onValidate: () => isPasswordStrong && onValidate(data),
    onSuccess
  });

  const onSubmit = e => {
    e.preventDefault();
    mutate();
  };

  return (
    <form onSubmit={onSubmit}>
      <UiKit.Grid templateColumns="1fr" gap="16">
        <UiKit.Input
          data-private="lipsum"
          label="Current Password"
          type="password"
          value={data.currentPassword}
          onChange={value => onChange({ currentPassword: value })}
          disabled={isProcessing}
          error={errors.currentPassword}
        />
        <UiKit.Input
          data-private="lipsum"
          label="New Password"
          type="password"
          value={data.newPassword}
          onChange={value => onChange({ newPassword: value })}
          disabled={isProcessing}
          error={errors.newPassword}
        />
        <PasswordStrength
          password={data.newPassword}
          onChange={setIsPasswordStrong}
        />
        <UiKit.Input
          data-private="lipsum"
          label="Confirm Password"
          type="password"
          value={data.confirmNewPassword}
          onChange={value => onChange({ confirmNewPassword: value })}
          disabled={isProcessing}
          error={errors.confirmNewPassword}
        />
        <div className={cn(styles.footer, { 'mt-3 pt-1': allowCancel })}>
          <If test={allowCancel}>
            <UiKit.Button
              size={buttonSize}
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isProcessing}
              className="ml-auto"
            >
              Cancel
            </UiKit.Button>
          </If>
          <UiKit.Button
            size={buttonSize}
            processing={isProcessing}
            disabled={!isPasswordStrong}
            className={cn({ 'w-100': !allowCancel })}
          >
            Update Password
          </UiKit.Button>
        </div>
      </UiKit.Grid>
    </form>
  );
}
